import React from "react";
import moment from "moment";
import submit from "submit-form";
import { withTranslation } from "react-i18next";
import ToolTip from "../../../../parts/tooltip";
import convert from "convert-units";
import convertToRiR from "../../../../utils/converters/convert-intensity";
import capitalize from "lodash/capitalize";
import { DATE_FORMATS } from "../../../../constants";
import SvgIcon from "../../../../parts/SvgIcon";
import { RillContext } from "../../../../rill/RillReact/share/base";

const { DATE_MONTH_DAY_WITHOUT_ZERO, DATE_DAY_FORMAT, DATE_PICKER_FORMAT } =
  DATE_FORMATS;

const numberWithCommas = (number) => {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const TARGET_VALUES = [
  "load",
  "reps",
  "sets",
  "rpe",
  "e1rm",
  "avgRpe",
  "avg1rm",
];

const SECTIONS_WITH_LABELS = [
  {
    valueLabel: "load",
    labelKey: "metricsDrawer.volume",
    suffix: "Kg",
  },
  {
    valueLabel: "avgRpe",
    labelKey: "metricsDrawer.avgRpe",
  },
  {
    valueLabel: "avg1rm",
    labelKey: "metricsDrawer.avgOneRmPercent",
    suffix: "%",
  },
  {
    valueLabel: "reps",
    labelKey: "metricsDrawer.numberOfLifts",
  },
  {
    valueLabel: "sets",
    labelKey: "metricsDrawer.numberOfSets",
  },
  {
    valueLabel: "e1rm",
    labelKey: "metricsDrawer.e1rm",
    suffix: "Kg",
  },
];

const getHigherValueFromArray = (
  arr,
  target = "totalOutcomes",
  workload = "load"
) => {
  return arr && arr.length
    ? arr.reduce((acc, curr) => {
        return acc[target][workload] > curr[target][workload] ? acc : curr;
      })
    : null;
};

const getDateAndValue = (arr, destinationLabel, field) => {
  const startWithYear = moment(arr.start, DATE_DAY_FORMAT).format(
    DATE_PICKER_FORMAT
  );
  const endWithYear = moment(arr.end, DATE_DAY_FORMAT).format(
    DATE_PICKER_FORMAT
  );
  return {
    range: `${startWithYear} - ${endWithYear}`,
    value: arr[destinationLabel][field],
  };
};

class MetricsDrawer extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      outcomes: [],
      targets: [],
      outcomesPeak: null,
      targetsPeak: null,
      peaks: null,
      selector: props.type === "outcomes" ? 0 : 1, // O = Actual - 1 = Target
      week: null,
      selectedWeekRange: null,
      workload: "load",
      timeRange: "all", // all - All Time, 1 - 1 Month, etc... (month units)
      chart: {
        labels: [],
        series: [],
      },
    };
    this.chartObject = null;
    this.loadChart = this.loadChart.bind(this);
    this.setValue = this.setValue.bind(this);
    this.renderHeader = this.renderHeader.bind(this);
    this.renderSwitch = this.renderSwitch.bind(this);
    this.renderChart = this.renderChart.bind(this);
    this.renderSelectors = this.renderSelectors.bind(this);
    this.renderSelectWeek = this.renderSelectWeek.bind(this);
    this.updateState = this.updateState.bind(this);
    this.hideE1RM = this.hideE1RM.bind(this);
  }

  setValue(field, value) {
    this.setState({
      [field]: value,
    });
  }

  loadChart(labels, series) {
    const Chartist = require("chartist");
    this.chartObject = new Chartist.Bar(
      ".drawer-chart",
      {
        labels,
        series: [series],
      },
      {
        fullWidth: true,
        seriesBarDistance: 10,
        showGrid: false,
        reverseData: true,
        horizontalBars: true,
        axisX: {
          // offset: -100,
          // showGrid: false,
          labelInterpolationFnc: function (value) {
            return value > 999 ? value / 1000 + "k" : value;
          },
        },
        axisY: {
          showGrid: false,
        },
      }
    ).on("draw", function (data) {
      if (data.type === "bar") {
        data.element.attr({
          style: "stroke-width: 10px; stroke: var(--information);",
        });
      }
      if (data.type === "label") {
        data.element.attr({
          style: "font-size: 13px;",
        });
      }
      if (data.type === "grid") {
        data.element.attr({
          style: "stroke: var(--white-10);",
        });
      }
    });
  }

  updateState(calendarMetrics, type) {
    const daysDiff = moment().diff(
      moment().add(-this.state.timeRange, "month"),
      "days"
    );

    const filterByTimeRange = (arr) =>
      arr.filter((data) => {
        const daysDiffInData = moment().diff(
          moment(data.start, DATE_DAY_FORMAT),
          "days"
        );
        return daysDiffInData <= daysDiff;
      });

    const destination = calendarMetrics[type];

    const totalDestinationLabel = `total${
      type === "outcomes" ? "Outcomes" : "Targets"
    }`;
    const filteredByTime =
      this.state.timeRange === "all"
        ? destination
        : filterByTimeRange(destination);

    const peaks = getHigherValueFromArray(
      filteredByTime,
      totalDestinationLabel,
      this.state.workload
    );

    const peakGroup = filteredByTime.length
      ? TARGET_VALUES.reduce((acc, curr) => {
          return Object.assign(acc, {
            [curr]: getDateAndValue(
              getHigherValueFromArray(
                filteredByTime,
                totalDestinationLabel,
                curr
              ),
              totalDestinationLabel,
              curr
            ),
          });
        }, {})
      : null;

    const [labels, series] = filteredByTime.slice(-6).reduce(
      (accum, data) => {
        const start = moment(data.start, DATE_DAY_FORMAT).format(
          DATE_MONTH_DAY_WITHOUT_ZERO
        );
        return [
          [...accum[0], start],
          [...accum[1], data[totalDestinationLabel][this.state.workload]],
        ];
      },
      [[], []]
    );

    this.setState({
      [type]:
        type === "outcomes" &&
        this.state.timeRange === "all" &&
        this.state.week === null &&
        moment(filteredByTime[0]?.start).isBefore(
          moment(filteredByTime[filteredByTime.length - 1]?.start)
        )
          ? filteredByTime
          : filteredByTime.reverse(),
      [`${type}Peak`]: peaks,
      peaks: peakGroup,
      lastWeeks: Object.assign({}, this.state.lastWeeks, {
        [type]: filteredByTime.slice(-6),
      }),
      chart: {
        labels,
        series,
      },
    });

    this.loadChart(labels, series);
  }

  componentDidMount() {
    const { type, calendarMetrics } = this.props;
    this.updateState(calendarMetrics, type);
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.timeRange !== this.state.timeRange ||
      prevProps.calendarMetrics !== this.props.calendarMetrics
    ) {
      const { calendarMetrics, type } = this.props;
      this.updateState(calendarMetrics, type);
    }
  }

  renderHeader() {
    const { t } = this.props;
    return (
      <header>
        <h2>{t("metricsDrawer.title")}</h2>
        <button
          onClick={() => {
            this.props.toggleMetricsDrawer(false);
          }}
          className="close"
        >
          <SvgIcon src="/img/new-icons/close-modal.svg" />
        </button>
      </header>
    );
  }

  renderSwitch() {
    const { t } = this.props;
    return (
      <div className="button-group">
        <button
          data-button-type={
            this.state.selector === 0 ? "outline" : "text-white"
          }
          onClick={() => {
            if (this.state.selector !== 0) {
              this.setValue("selector", 0);
              submit(`/actions/refresh/?`, {
                method: "GET",
                body: {
                  metrics: "outcomes",
                },
              });
            }
          }}
        >
          <span>{t("metricsDrawer.actual")}</span>
        </button>
        <button
          data-button-type={
            this.state.selector === 1 ? "outline" : "text-white"
          }
          onClick={() => {
            if (this.state.selector !== 1) {
              this.setValue("selector", 1);
              submit(`/actions/refresh/?`, {
                method: "GET",
                body: {
                  metrics: "targets",
                },
              });
            }
          }}
        >
          <span>{t("metricsDrawer.targets")}</span>
        </button>
      </div>
    );
  }

  renderSelectors() {
    const { t } = this.props;
    return (
      <section>
        <p className="s2">{t("metricsDrawer.dataToReportOn")}</p>
        <div className="workload-header">
          <div className="sub-selector first">
            <label htmlFor="exercise-selector">{t("metricsDrawer.exercise")}</label>
            <select
              id="exercise-selector"
              onChange={(evt) => {
                submit(`/actions/refresh/?`, {
                  method: "GET",
                  body: {
                    exercise: evt.target.value,
                  },
                });
              }}
            >
              <option value="all">{t("metricsDrawer.allExercises")}</option>
              {this.props.calendarMetrics.exerciseList.map((exercise, idx) => (
                <option
                  value={`${exercise.primary}-${exercise.secondary}`}
                  key={idx}
                >
                  {exercise.primary === "assistant"
                    ? `${capitalize(exercise.secondary)}`
                    : `${capitalize(exercise.primary)}${
                        exercise.secondary
                          ? ` (${capitalize(exercise.secondary)})`
                          : ""
                      }`}
                </option>
              ))}
            </select>
          </div>
          <div className="sub-selector">
            <label htmlFor="date-range-selector">{t("metricsDrawer.dateRange")}</label>
            <select
              id="date-range-selector"
              onChange={(evt) => {
                this.setValue(
                  "timeRange",
                  evt.target.value === "all"
                    ? "all"
                    : parseInt(evt.target.value || 1)
                );
              }}
            >
              <option value="all">{t("metricsDrawer.allTime")}</option>
              <option value="1">{t("metricsDrawer.oneMonth")}</option>
              <option value="3">{t("metricsDrawer.threeMonths")}</option>
              <option value="6">{t("metricsDrawer.sixMonths")}</option>
              <option value="12">{t("metricsDrawer.oneYear")}</option>
            </select>
          </div>
        </div>
      </section>
    );
  }

  renderSubDivision({
    value = 0,
    suffix = null,
    title = "",
    boldTitle = false,
    idx = 0,
  }) {
    if (value === 0) return null;
    return (
      <div className="sub-division" key={idx}>
        <p className="p2">{title}</p>
        <p className="p1">
          <span>
            {numberWithCommas(value)} {suffix || ""}
          </span>
        </p>
      </div>
    );
  }

  hideE1RM(valueLabel) {
    const { selector } = this.state;
    const { exerciseSelected } = this.props;
    return (
      !(
        valueLabel === "e1rm" &&
        selector === 0 &&
        exerciseSelected === "all"
      ) && !(valueLabel === "e1rm" && selector === 1)
    );
  }

  renderPeaks() {
    const { peaks, selector, outcomesPeak, targetsPeak } = this.state;

    const noPeak =
      (selector === 0 && (!outcomesPeak || !outcomesPeak.totalOutcomes)) ||
      (selector === 1 && (!targetsPeak || !targetsPeak.totalTargets));

    if (noPeak) {
      return (
        <div
          style={{
            margin: "8px 12px",
          }}
        >
          {selector === 0 ? this.props.t("metricsDrawer.noOutcomesFound") : this.props.t("metricsDrawer.noTargetsFound")}
        </div>
      );
    }

    return (
      <section>
        <p className="s2">{this.props.t("metricsDrawer.highestWeeklyMetrics")}</p>
        {peaks
          ? SECTIONS_WITH_LABELS.filter((val) => {
              return this.hideE1RM(val.valueLabel);
            }).map(({ valueLabel, labelKey, suffix }, idx) => {
              const isLoadAndImperial =
                (valueLabel === "load" || valueLabel === "e1rm") &&
                this.props.userUnit === "imperial";

              const value = isLoadAndImperial
                ? convert(peaks[valueLabel].value)
                    .from("kg")
                    .to("lb")
                    .toFixed(2)
                : peaks[valueLabel].value;

              const suffixFinal = isLoadAndImperial
                ? this.props.t("metricsDrawer.lbs")
                : suffix;
              const convertValue =
                this.props.isRiR &&
                labelKey === "metricsDrawer.avgRpe";
              const finalLabel = convertValue
                ? this.props.t("metricsDrawer.avgRir")
                : this.props.t(labelKey);

              return (
                <ToolTip
                  key={idx}
                  id={`xd-${idx}`}
                  body={this.props.t("metricsDrawer.weekTooltip", {
                    range: peaks[valueLabel].range,
                  })}
                >
                  {this.renderSubDivision({
                    title: finalLabel,
                    value: convertValue
                      ? convertToRiR(Math.round(value), !this.props.isRiR)
                      : Math.round(value),
                    suffix: suffixFinal,
                    idx: `${idx}-peak`,
                  })}
                </ToolTip>
              );
            })
          : null}
      </section>
    );
  }

  renderSelectWeek() {
    const { selector, week, outcomes, targets, timeRange, selectedWeekRange } =
      this.state;
    const weekStart = week;

    const metricsData = (selector === 0 ? outcomes : targets).find((_o) =>
      weekStart ? _o.start === weekStart : true
    );
    const selectedData = metricsData
      ? metricsData[selector === 0 ? "totalOutcomes" : "totalTargets"]
      : null;

    if (!selectedData) return null;

    return (
      <section>
        <p className="s2">{this.props.t("metricsDrawer.weekToAnalyze")}</p>
        <div className="sub-division">
          <label htmlFor="selected-week">{this.props.t("metricsDrawer.selectedWeek")}</label>
          <select
            id="selected-week"
            onChange={(evt) => {
              this.setValue("week", evt.target.value);
              this.setValue("selectedWeekRange", evt.target.value);
            }}
            value={
              selectedWeekRange === null
                ? selector === 0 && timeRange === "all" && week === null
                  ? outcomes[outcomes.length - 1].start
                  : selector === 0
                  ? outcomes[0].start
                  : targets[0].start
                : selectedWeekRange
            }
          >
            {(selector === 0 ? outcomes : targets).map((outcome, idx) => {
              return (
                <option value={outcome.start} key={idx}>
                  {outcome.range}
                </option>
              );
            })}
          </select>
        </div>

        {SECTIONS_WITH_LABELS.filter((val) => {
          return this.hideE1RM(val.valueLabel);
        }).map(({ valueLabel, labelKey, suffix }, idx) => {
          const isLoadAndImperial =
            (valueLabel === "load" || valueLabel === "e1rm") &&
            this.props.userUnit === "imperial";

          const value = isLoadAndImperial
            ? convert(selectedData[valueLabel]).from("kg").to("lb").toFixed(2)
            : selectedData[valueLabel];

          const suffixFinal = isLoadAndImperial
            ? this.props.t("metricsDrawer.lbs")
            : suffix;
          const convertValue =
            this.props.isRiR &&
            labelKey === "metricsDrawer.avgRpe";
          const finalLabel = convertValue
            ? this.props.t("metricsDrawer.avgRir")
            : this.props.t(labelKey);

          return this.renderSubDivision({
            title: finalLabel,
            value: convertValue
              ? convertToRiR(Math.round(value), !this.props.isRiR)
              : Math.round(value),
            suffix: suffixFinal,
            idx,
          });
        })}
      </section>
    );
  }

  renderChart() {
    const { outcomes, targets, workload } = this.state;
    const selector = this.state.selector;

    return (
      <footer
        style={
          (selector === 0 && outcomes && outcomes.length) ||
          (selector === 1 && targets && targets.length)
            ? {}
            : {
                display: "none",
              }
        }
      >
        <p className="s2">
          {this.props.t("metricsDrawer.pastSixWeekVolume", {
            metric:
              workload === "load"
                ? this.props.t("metricsDrawer.volume")
                : workload,
          })}
        </p>
        <p className="p1 mt-4">{this.props.t("metricsDrawer.weekOf")}</p>
        <div className="drawer-chart" />
      </footer>
    );
  }

  render() {
    return (
      <>
        <div className="metrics-drawer side-drawer open">
          {this.renderHeader()}
          {this.renderSwitch()}
          {this.renderSelectors()}
          {this.renderPeaks()}
          {this.renderSelectWeek()}
          {this.renderChart()}
        </div>
        <div
          className="drawer-backdrop"
          onClick={() => this.props.toggleMetricsDrawer(false)}
        />
      </>
    );
  }
}

MetricsDrawer.contextType = RillContext;

export default withTranslation("athlete-portal")(MetricsDrawer);
